import { createContext, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Event,
    EventGroup,
    EventTodo,
    queryEventDetails,
    queryEventTodo,
    StationType,
    TodoType,
    WasteMeasurementWithMaterial,
} from './schema';
import { getOneLink } from './utils';
import L from 'leaflet';

interface IMapContext {
    editMode: boolean;
    todoMode: boolean;
    toggleEditMode: () => void;
    toggleTodoMode: () => void;
    cleanupMode: boolean;
    toggleCleanupMode: () => void;
    currentStation: StationType | null;
    setCurrentStation: (icon: StationType | null) => void;
    currentTodoType: TodoType | null;
    setCurrentTodoType: (type: TodoType | null) => void;
    eventId: string | undefined;
    mapNumber: number;
    sweepRect: L.LatLngBounds | null;
    setSweepRect: (rect: L.LatLngBounds | null) => void;
    selectedStationTypeIds: Set<string>;
    setSelectedStationTypeIds: (ids: Set<string>) => void;
    todoOnlyFilter: boolean;
    setTodoOnlyFilter: (enabled: boolean) => void;
    notesOnlyFilter: boolean;
    setNotesOnlyFilter: (enabled: boolean) => void;
}

export interface IWasteDataContext {
    datasetId: string;
    editMode: boolean;
    todoMode: boolean;
    toggleEditMode: () => void;
    toggleTodoMode: () => void;
    selectedRow: WasteMeasurementWithMaterial | null;
    setSelectedRow: (id: WasteMeasurementWithMaterial | null) => void;
}

const defaultSetCurrentIcon = () => console.error('OOPS: setCurrentStation undefined');
const defaultSetCurrentTodoType = () => console.error('OOPS: setCurrentTodoType undefined');
const defaultSetSweepRect = () => console.error('OOPS: setSweepRect undefined');
const defaultSetSelectedStationTypeIds = () => console.error('OOPS: setSelectedStationTypeIds undefined');
const defaultSetTodoOnlyFilter = () => console.error('OOPS: setTodoOnlyFilter undefined');
const defaultSetNotesOnlyFilter = () => console.error('OOPS: setNotesOnlyFilter undefined');

export const MapContext = createContext<IMapContext>({
    editMode: false,
    todoMode: false,
    toggleEditMode: () => {},
    toggleTodoMode: () => {},
    cleanupMode: false,
    toggleCleanupMode: () => {},
    currentStation: null,
    setCurrentStation: defaultSetCurrentIcon,
    currentTodoType: null,
    setCurrentTodoType: defaultSetCurrentTodoType,
    eventId: undefined,
    mapNumber: 0,
    sweepRect: null,
    setSweepRect: defaultSetSweepRect,
    selectedStationTypeIds: new Set(),
    setSelectedStationTypeIds: defaultSetSelectedStationTypeIds,
    todoOnlyFilter: false,
    setTodoOnlyFilter: defaultSetTodoOnlyFilter,
    notesOnlyFilter: false,
    setNotesOnlyFilter: defaultSetNotesOnlyFilter,
});

export const WasteDataContext = createContext<IWasteDataContext>({
    datasetId: '',
    editMode: false,
    todoMode: false,
    toggleEditMode: () => {},
    toggleTodoMode: () => {},
    selectedRow: null,
    setSelectedRow: () => {},
});

interface IEventsContext {
    events: Map<string, Event>;
    eventGroups: Map<string, EventGroup>;
}

interface IScrollContext {
    scrollToBottom: () => void;
}

export const EventsContext = createContext<IEventsContext>({
    events: new Map(),
    eventGroups: new Map(),
});

export const ScrollContext = createContext<IScrollContext>({
    scrollToBottom: () => {},
});

export const ViewOnlyContext = createContext<boolean>(false);

export const useEventGroup = (groupId?: string): EventGroup | undefined => {
    const { eventGroups } = useContext(EventsContext);
    if (groupId) {
        const eventGroup = eventGroups.get(groupId);
        return eventGroup;
    }
};

export const useEventGroupList = (): EventGroup[] => {
    const { eventGroups } = useContext(EventsContext);
    return Array.from(eventGroups, ([_k, v]) => v);
};

export const useEvent = (eventId?: string | null): Event | undefined => {
    const { events } = useContext(EventsContext);
    if (eventId) {
        const event = events.get(eventId);
        return event;
    }
};

// Get a map from an event
export const useEventMap = (eventId?: string | null, mapNumber: number = 0) => {
    const { event, isLoading, error } = useEventDetails(eventId);

    // Get the specific map based on the mapNumber
    const map = event?.maps?.[mapNumber];

    return {
        map,
        isLoading,
        error,
    };
};

export const useEventsList = (): Event[] => {
    const { events } = useContext(EventsContext);
    // convert map to array
    const eventsList = Array.from(events, ([_k, v]) => v);
    return eventsList;
};

// Get the map of ids to events
export const useEventsMap = (): Map<string, Event> => {
    const { events } = useContext(EventsContext);
    return events;
};

export const useEventDetails = (eventId?: string | null) => {
    const viewOnly = useContext(ViewOnlyContext);
    const [searchParams] = useSearchParams();
    const secret = viewOnly ? (searchParams.get('secret') ?? undefined) : undefined;
    const { data, isLoading, error } = queryEventDetails(eventId, { secret });

    const event = getOneLink(data?.events);

    return {
        event,
        isLoading,
        error,
    };
};

export const useEventTodo = (eventId?: string | null, type?: string | null, completed?: boolean | null) => {
    const viewOnly = useContext(ViewOnlyContext);
    const [searchParams] = useSearchParams();
    const secret = viewOnly ? (searchParams.get('secret') ?? undefined) : undefined;
    const { data, isLoading, error } = queryEventTodo(eventId, type ?? undefined, completed ?? undefined, { secret });

    const event = getOneLink(data?.events);
    const todo: EventTodo['todos'] = event?.todos || [];

    const todoIsLoading = isLoading;
    const todoError = error;
    return {
        todo,
        todoIsLoading,
        todoError,
    };
};
